import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

export default function Cart() {
  const { cart, update, remove } = useCart();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <p className="text-[#767676] mb-4">{t("empty")}</p>
        <Link to="/login">
          <Button className="rounded-full bg-[#1A1738] text-white">{t("signIn")}</Button>
        </Link>
      </div>
    );
  }

  return (
    <main data-testid="cart-page" className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <h1 className="font-display text-3xl md:text-4xl mb-8">{t("yourCart")}</h1>

      {cart.items.length === 0 ? (
        <div className="py-16 text-center text-[#767676]" data-testid="cart-empty">
          <p className="mb-4">{t("empty")}</p>
          <Link to="/shop">
            <Button variant="outline" className="rounded-full">{t("continueShopping")}</Button>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-[1fr,320px] gap-10">
          <div className="divide-y divide-[#E5E1D8]">
            {cart.items.map((it) => {
              const p = it.product;
              const name = lang === "ar" ? p.name_ar : p.name_en;
              return (
                <div key={p.id} className="py-5 flex gap-4" data-testid={`cart-item-${p.id}`}>
                  <Link to={`/product/${p.id}`} className="shrink-0">
                    <img src={p.image} alt={name} className="h-24 w-24 object-cover rounded-md bg-[#F3F0EB]" />
                  </Link>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#7B5CBF]">{p.brand}</div>
                    <Link to={`/product/${p.id}`} className="font-display text-lg hover-underline">{name}</Link>
                    <div className="mt-1 text-sm text-[#767676]">${p.final_price.toFixed(2)}</div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex items-center border border-[#E5E1D8] rounded-full text-sm">
                        <button
                          data-testid={`cart-decr-${p.id}`}
                          onClick={() => update(p.id, it.quantity - 1)}
                          className="w-8 h-8"
                        >−</button>
                        <span className="w-8 text-center" data-testid={`cart-qty-${p.id}`}>{it.quantity}</span>
                        <button
                          data-testid={`cart-incr-${p.id}`}
                          onClick={() => update(p.id, it.quantity + 1)}
                          className="w-8 h-8"
                        >+</button>
                      </div>
                      <button
                        onClick={() => remove(p.id)}
                        data-testid={`cart-remove-${p.id}`}
                        className="text-[#767676] hover:text-[#1A1738] p-2"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <div className="text-end font-medium" data-testid={`cart-line-${p.id}`}>
                    ${it.line_total.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="border border-[#E5E1D8] rounded-md p-6 h-fit bg-white">
            <div className="text-xs uppercase tracking-widest text-[#767676] mb-4">{t("total")}</div>
            <div className="text-3xl font-display" data-testid="cart-total">${cart.total.toFixed(2)}</div>
            <div className="mt-6 text-xs text-[#767676]">
              {t("walletBalance")}: ${user.wallet_balance.toFixed(2)}
            </div>
            {cart.total < 300 && (
              <div className="mt-3 p-3 bg-[#F3F0EB] rounded text-xs text-[#7B5CBF]" data-testid="cart-min-msg">
                {t("minOrder")}: $300.00 · {t("addMore").replace("${amount}", `$${(300 - cart.total).toFixed(2)}`)}
              </div>
            )}
            <Button
              data-testid="checkout-btn"
              onClick={() => navigate("/checkout")}
              disabled={cart.total < 300}
              className="w-full mt-6 rounded-full bg-[#1A1738] hover:bg-[#2C2650] text-white h-11 disabled:opacity-50"
            >
              {t("checkout")}
            </Button>
            <Link to="/shop" className="block text-center mt-4 text-sm hover-underline">
              {t("continueShopping")}
            </Link>
          </aside>
        </div>
      )}
    </main>
  );
}
